import type { RefObject } from 'react';
import type { MouseEvent } from 'react';
import {
  Box,
  Menu,
  MenuItem,
  Typography,
  CircularProgress,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import PlayIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import { useTranslation } from 'react-i18next';

import type { ScriptStep } from '@mytaverse-shared/interfaces/assistantScripts';
import type { PollyLanguage } from '@mytaverse-shared/interfaces/polly';
import { replaceAssistantPlaceholders } from '@mytaverse-shared/utils/assistantScript';
import { languageFlags, languageLabels } from '../constants';
import { useModalTheme } from '@mytaverse-shared/components/AssistantEditorModals/context';
import { modalThemeTokens } from '@mytaverse-shared/components/AssistantEditorModals/theme';
import type { AudioPlaybackKey } from '../hooks/useAudioPreview';
import {
  AutoTranslateButton,
  LanguageRowsList,
  LanguageRowItem,
  LanguageRowFlag,
  LanguageRowName,
  BaseBadge,
  ProgressWrap,
  ProgressTrack,
  ProgressFill,
  ProgressCountText,
  PreviewIconButton,
} from './styled';

export const isLanguageComplete = (
  steps: ScriptStep[],
  lang: PollyLanguage,
): boolean =>
  steps.length > 0 &&
  steps.every((s) => {
    const t = s.translations?.[lang];
    return typeof t === 'string' && t.trim().length > 0;
  });

interface LanguageTabsProps {
  scriptLanguages: PollyLanguage[];
  effectiveBaseLanguage: PollyLanguage;
  activeLanguage: PollyLanguage;
  scriptSteps: ScriptStep[];
  isTranslating: boolean;
  translateMenuAnchor: HTMLElement | null;
  // The dialogue-steps form rendered below this list — clicking a row
  // scrolls it into view so the user immediately sees what they selected.
  stepsFormRef: RefObject<HTMLDivElement>;
  audioPlaybackKey: AudioPlaybackKey | null;
  isAudioPlaying: boolean;
  userFirstName?: string;
  mapName?: string;
  onActiveLanguageChange: (lang: PollyLanguage) => void;
  onTranslateAll: () => void;
  onTranslateMenuOpen: (anchor: HTMLElement) => void;
  onTranslateMenuClose: () => void;
  onPickLanguages: () => void;
  onPlay: (
    text: string,
    key: AudioPlaybackKey,
    language: PollyLanguage,
  ) => void;
  onStop: () => void;
  // Suppresses the row highlight so the tab strip opens with nothing marked
  // selected, even though activeLanguage itself still holds a real value.
  forceNoSelection?: boolean;
  // How many language rows sit side by side per grid row. Defaults to 1 (the
  // original single-column list).
  itemsPerRow?: 1 | 2 | 3;
}

const LanguageTabs = ({
  scriptLanguages,
  effectiveBaseLanguage,
  activeLanguage,
  scriptSteps,
  isTranslating,
  translateMenuAnchor,
  stepsFormRef,
  audioPlaybackKey,
  isAudioPlaying,
  userFirstName,
  mapName,
  onActiveLanguageChange,
  onTranslateAll,
  onTranslateMenuOpen,
  onTranslateMenuClose,
  onPickLanguages,
  onPlay,
  onStop,
  forceNoSelection,
  itemsPerRow = 2,
}: LanguageTabsProps) => {
  const { t } = useTranslation('common');
  const modalTheme = useModalTheme();
  const tokens = modalThemeTokens[modalTheme];
  const translateDisabled =
    isTranslating || !scriptSteps.some((s) => s.text.trim());

  const handleRowClick = (lang: PollyLanguage) => {
    onActiveLanguageChange(lang);
    stepsFormRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  return (
    <Box sx={{ mb: forceNoSelection ? 0 : 6 }}>
      <Box sx={{ mb: 2.25, display: 'flex' }}>
        <AutoTranslateButton
          variant="contained"
          size="small"
          onClick={onTranslateAll}
          disabled={translateDisabled}
          data-testid="script-translate-all-btn"
          startIcon={
            isTranslating ? (
              <CircularProgress size={13} color="inherit" />
            ) : undefined
          }
          sx={{
            height: '34px',
            fontSize: '13px',
            padding: '0 16px !important',
            borderRadius: '9px 0 0 9px !important',
            borderRight: 'none !important',
          }}
        >
          {isTranslating
            ? t('assistant.scriptDialog.translating')
            : t('assistant.scriptDialog.autoTranslateAllLanguages')}
        </AutoTranslateButton>
        <AutoTranslateButton
          variant="contained"
          size="small"
          onClick={(e) => onTranslateMenuOpen(e.currentTarget)}
          disabled={translateDisabled}
          data-testid="script-translate-auto-btn"
          sx={{
            height: '34px',
            padding: '0 8px !important',
            borderRadius: '0 9px 9px 0 !important',
            minWidth: 'unset',
          }}
        >
          <ArrowDropDownIcon sx={{ fontSize: 18 }} />
        </AutoTranslateButton>
        <Menu
          anchorEl={translateMenuAnchor}
          open={Boolean(translateMenuAnchor)}
          onClose={onTranslateMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{
            paper: {
              sx: {
                borderRadius: 2,
                mt: 0.5,
                minWidth: 190,
                background: tokens.surface,
                border: tokens.inlineBtnBorder,
                '& .MuiMenuItem-root': {
                  color: tokens.inlineBtnColor,
                  '&:hover': { backgroundColor: tokens.inlineBtnHoverBg },
                },
              },
            },
          }}
        >
          <MenuItem
            onClick={() => {
              onTranslateMenuClose();
              onPickLanguages();
            }}
            data-testid="script-translate-pick-languages-item"
            sx={{
              gap: 1.5,
              alignItems: 'center',
              height: 34,
              minHeight: 34,
              py: 0,
            }}
          >
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 500,
                color: tokens.menuAccentColor,
              }}
            >
              {t('assistant.scriptDialog.pickLanguages')}
            </Typography>
          </MenuItem>
        </Menu>
      </Box>

      <LanguageRowsList $itemsPerRow={itemsPerRow}>
        {scriptLanguages.map((lang) => {
          const isBase = lang === effectiveBaseLanguage;
          const selected = !forceNoSelection && activeLanguage === lang;
          const totalCount = scriptSteps.length;

          let complete: boolean;
          let translatedCount: number;

          if (isBase) {
            complete =
              totalCount > 0 &&
              scriptSteps.every((s) => s.text.trim().length > 0);
            translatedCount = scriptSteps.filter(
              (s) => s.text.trim().length > 0,
            ).length;
          } else {
            complete = isLanguageComplete(scriptSteps, lang);
            translatedCount = scriptSteps.filter((s) => {
              const translation = s.translations?.[lang];
              return (
                typeof translation === 'string' && translation.trim().length > 0
              );
            }).length;
          }

          const progressState = complete
            ? 'done'
            : translatedCount > 0
            ? 'partial'
            : 'empty';
          const progressPct =
            totalCount > 0 ? (translatedCount / totalCount) * 100 : 0;

          // Only the first step is ever previewed here, so visibility and
          // playback are both driven off that step alone rather than the
          // language's overall completeness.
          const firstStepRawText = isBase
            ? scriptSteps[0]?.text
            : scriptSteps[0]?.translations?.[lang];
          const hasPreviewText = Boolean(firstStepRawText?.trim());
          const previewKey: AudioPlaybackKey = `lang:${lang}`;
          const isPreviewActive = audioPlaybackKey === previewKey;
          const isPreviewLoading = isPreviewActive && !isAudioPlaying;
          const isPreviewPlaying = isPreviewActive && isAudioPlaying;
          const previewDisabled =
            audioPlaybackKey !== null && audioPlaybackKey !== previewKey;

          const handlePreviewClick = (e: MouseEvent) => {
            e.stopPropagation();

            if (isPreviewPlaying) {
              onStop();
              return;
            }

            onPlay(
              replaceAssistantPlaceholders(
                firstStepRawText ?? '',
                userFirstName,
                mapName,
              ),
              previewKey,
              lang,
            );
          };

          return (
            <LanguageRowItem
              key={lang}
              $selected={selected}
              onClick={() => handleRowClick(lang)}
              data-testid={`script-language-tab-${lang}`}
            >
              <LanguageRowFlag>{languageFlags[lang]}</LanguageRowFlag>
              <LanguageRowName>{languageLabels[lang]}</LanguageRowName>
              {hasPreviewText && (
                <PreviewIconButton
                  onClick={handlePreviewClick}
                  disabled={previewDisabled}
                  aria-label={t(
                    isPreviewPlaying
                      ? 'assistant.scriptDialog.previewStop'
                      : 'assistant.scriptDialog.previewPlay',
                  )}
                >
                  {isPreviewLoading ? (
                    <CircularProgress size={12} color="inherit" />
                  ) : isPreviewPlaying ? (
                    <StopIcon sx={{ fontSize: 15 }} />
                  ) : (
                    <PlayIcon sx={{ fontSize: 15 }} />
                  )}
                </PreviewIconButton>
              )}
              {isBase ? (
                <BaseBadge
                  sx={{
                    border:
                      modalTheme === 'light' ? '1px solid #dddbdb' : undefined,
                  }}
                >
                  {t('assistant.scriptDialog.baseBadge')}
                </BaseBadge>
              ) : (
                <ProgressWrap>
                  <ProgressTrack>
                    <ProgressFill
                      $state={progressState}
                      sx={{ width: `${progressPct}%` }}
                      data-testid={`script-language-tab-${lang}-progress-bar`}
                    />
                  </ProgressTrack>
                  <ProgressCountText
                    $state={progressState}
                    data-testid={`script-language-tab-${lang}-progress-text`}
                  >
                    {complete ? '✓' : `${translatedCount}/${totalCount}`}
                  </ProgressCountText>
                </ProgressWrap>
              )}
            </LanguageRowItem>
          );
        })}
      </LanguageRowsList>
    </Box>
  );
};

export default LanguageTabs;
